import { ButtonHTMLAttributes, FC, ReactNode, useEffect, useRef } from 'react';

import IconButton from './IconButton';

export type MenuProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The trigger button's accessible name. */
  label: string;
  children: ReactNode;
  /** Placement only: the trigger wrap's layout is the menu's. */
  className?: string;
};

export type MenuItemProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  /** The destructive tone, for an action that removes something. */
  danger?: boolean;
  children: ReactNode;
};

/** One action inside a `Menu`. */
export const MenuItem: FC<MenuItemProps> = ({ danger = false, className, children, ...rest }) => (
  <button
    type="button"
    role="menuitem"
    {...rest}
    className={['ui-menu-item', danger && 'ui-menu-item--danger', className].filter(Boolean).join(' ')}
  >
    {children}
  </button>
);

/**
 * An anchored popover menu: a trigger and a surface of items.
 *
 * It owns the open-state wiring only - trigger `aria-haspopup`/`aria-expanded`,
 * Escape and outside-pointer close, and arrow/Home/End focus movement - while
 * the caller names the trigger and supplies the items. It carries no page
 * meaning: the surface, item shape and keyboard are the same everywhere.
 */
const Menu: FC<MenuProps> = ({ open, onOpenChange, label, children, className }) => {
  const wrapRef = useRef<HTMLDivElement>(null);
  const surfaceRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const closeOnOutside = (event: Event) => {
      if (!wrapRef.current?.contains(event.target as Node)) {
        onOpenChange(false);
      }
    };

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onOpenChange(false);
        wrapRef.current?.querySelector<HTMLButtonElement>('button')?.focus();
        return;
      }

      const items = Array.from(surfaceRef.current?.querySelectorAll<HTMLButtonElement>('[role="menuitem"]:not(:disabled)') ?? []);
      if (items.length === 0) {
        return;
      }
      const index = items.findIndex((item) => item === document.activeElement);

      if (event.key === 'ArrowDown') {
        event.preventDefault();
        items[(index + 1 + items.length) % items.length]?.focus();
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        items[index <= 0 ? items.length - 1 : index - 1]?.focus();
      } else if (event.key === 'Home') {
        event.preventDefault();
        items[0]?.focus();
      } else if (event.key === 'End') {
        event.preventDefault();
        items[items.length - 1]?.focus();
      }
    };

    document.addEventListener('pointerdown', closeOnOutside);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', closeOnOutside);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open, onOpenChange]);

  return (
    <div ref={wrapRef} className={['ui-menu', className].filter(Boolean).join(' ')}>
      <IconButton label={label} aria-haspopup="menu" aria-expanded={open} onClick={() => onOpenChange(!open)}>
        <span aria-hidden="true">⋯</span>
      </IconButton>
      {open && (
        <div ref={surfaceRef} className="ui-menu-surface" role="menu">
          {children}
        </div>
      )}
    </div>
  );
};

export default Menu;
